/* eslint-disable import/no-cycle */
import { createSlice, createEntityAdapter } from '@reduxjs/toolkit';
import { jwtDecode } from 'jwt-decode';
import { getCurrentUserInvitations } from '../thunks/invitations';
import { getCurrentUser } from '../thunks/user';
import {
  updateWorkspaceById,
  getWorkspaceByIdAsSuperAdmin,
} from '../thunks/workspaces';
import { getDeviceById } from '../thunks/devices';

const workspacesAdapter = createEntityAdapter({});

export const workspacesSlice = createSlice({
  name: 'workspacesReducer',
  initialState: workspacesAdapter.getInitialState({
    loading: false,
    invitations: [],
    // Superadmin lookup of a workspace the user is not a member of.
    // Deliberately separate from `loading` above, which drives isWorkspaceLoading
    // in four route guards — a stuck value there would lock the whole app.
    superAdminLookup: { id: null, status: 'idle' },
  }),
  reducers: {
    setWorkspaces: workspacesAdapter.setAll,
    updateWorkspaceLocally: (state, action) => {
      const { id } = action.payload;

      workspacesAdapter.updateOne(state, {
        id,
        changes: action.payload,
      });
    },
    removeWorkspaces: workspacesAdapter.removeAll,
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCurrentUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(getCurrentUser.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(getWorkspaceByIdAsSuperAdmin.pending, (state, action) => {
        state.superAdminLookup = { id: action.meta.arg, status: 'loading' };
      })
      .addCase(getWorkspaceByIdAsSuperAdmin.fulfilled, (state, action) => {
        workspacesAdapter.upsertOne(state, action.payload);
        state.superAdminLookup = { id: action.payload.id, status: 'done' };
      })
      .addCase(getWorkspaceByIdAsSuperAdmin.rejected, (state, action) => {
        state.superAdminLookup = { id: action.meta.arg, status: 'failed' };
      })
      .addCase(getDeviceById.fulfilled, (state, action) => {
        const { workspace } = action.payload;
        if (workspace) {
          workspacesAdapter.upsertOne(state, workspace);
        }
      })
      .addCase(updateWorkspaceById.fulfilled, (state, action) => {
        const { id } = action.payload;
        workspacesAdapter.updateOne(state, {
          id,
          changes: action.payload,
        });
      })
      .addCase(getCurrentUserInvitations.fulfilled, (state, action) => {
        const fetchedInvitations = action.payload;
        const newInvitations = fetchedInvitations.map((invitation) => {
          const decodedInvitationToken = jwtDecode(invitation.token);
          return {
            ...invitation,
            name: decodedInvitationToken.resourceName,
          };
        });

        state.invitations = [...newInvitations];
      });
  },
});

export const { setWorkspaces, updateWorkspaceLocally, removeWorkspaces } =
  workspacesSlice.actions;

export const workspacesSelectors = workspacesAdapter.getSelectors(
  (state) => state.workspaces
);

export default workspacesSlice.reducer;
